import { LEAD_SOURCE_NONE } from "../../../Leads/constants/lead-source-none.ts"
import type {
  LeadFormData,
  LeadFormDataCustomer
} from "../components/lead-form-data.ts"
import type { LinkedCustomer } from "./linked-customer.ts"

const WIZARD_CUSTOMER_FIELDS = [
  "first_name",
  "last_name",
  "phone",
  "email"
] as const

const WIZARD_PROJECT_TEXT_FIELDS = [
  "lead_name",
  "address",
  "address_2",
  "city",
  "state",
  "zip_code",
  "source_comment",
  "architect_name",
  "comments"
] as const

type WizardCustomerField = (typeof WIZARD_CUSTOMER_FIELDS)[number]

interface CreateLeadPayload
  extends Partial<Pick<LeadFormData, (typeof WIZARD_PROJECT_TEXT_FIELDS)[number]>> {
  source: string
  project_type?: string[]
  use_customer_address?: boolean
  customer?: Partial<Record<WizardCustomerField, string>>
  customer_id?: string
}

function trimmed(value: unknown): string {
  return typeof value === "string" ? value.trim() : ""
}

function customerFields(
  customer: LeadFormDataCustomer | null | undefined
): Partial<Record<WizardCustomerField, string>> {
  const result: Partial<Record<WizardCustomerField, string>> = {}

  for (const field of WIZARD_CUSTOMER_FIELDS) {
    const value = trimmed(customer?.[field])
    if (value) result[field] = value
  }

  return result
}

/**
 * Only what the user changed after picking the customer. Linking says "this is
 * that customer"; resending the untouched record as nested values would ask the
 * server to write the customer back over itself on every lead created for them.
 * A field emptied after linking is not a correction — the server reads a blank
 * as "erase this", which is never what clearing a prefilled box meant here.
 */
function customerCorrections(
  entered: Partial<Record<WizardCustomerField, string>>,
  linked: LeadFormDataCustomer
): Partial<Record<WizardCustomerField, string>> {
  const corrections: Partial<Record<WizardCustomerField, string>> = {}

  for (const field of WIZARD_CUSTOMER_FIELDS) {
    const value = entered[field] ?? ""
    if (value && value !== trimmed(linked[field])) corrections[field] = value
  }

  return corrections
}

/**
 * Builds the single POST the wizard makes. The stage is deliberately absent —
 * the backend defaults a new lead to New, and naming it here would let the
 * client drift from that default.
 */
function buildCreateLeadPayload(
  values: LeadFormData,
  linked: LinkedCustomer | null
): CreateLeadPayload {
  const payload: CreateLeadPayload = {
    source: trimmed(values.source) || LEAD_SOURCE_NONE
  }

  for (const field of WIZARD_PROJECT_TEXT_FIELDS) {
    const value = trimmed(values[field])
    if (value) payload[field] = value
  }

  const projectTypes = (values.project_type ?? []).filter(Boolean)
  if (projectTypes.length) payload.project_type = projectTypes

  if (values.use_customer_address) payload.use_customer_address = true

  const entered = customerFields(values.customer)

  if (linked?.id) {
    payload.customer_id = linked.id
    const corrections = customerCorrections(entered, linked.customer)
    if (Object.keys(corrections).length) payload.customer = corrections
    return payload
  }

  if (Object.keys(entered).length) payload.customer = entered

  return payload
}

export {
  type CreateLeadPayload,
  WIZARD_CUSTOMER_FIELDS,
  WIZARD_PROJECT_TEXT_FIELDS,
  buildCreateLeadPayload
}
